/**
 * Canvas engine for the founder bottleneck (step 01). Thin routes from the
 * team bend into one funnel and their particles pile into a knot at the neck.
 * Questions arrive along the askers' routes faster and faster; the knot swells
 * and the backlog creeps back up the funnel, while the neck fires each question
 * at the founder in quicker and quicker shots until they merge into a stream.
 * The founder fills up, strains, spills over and bursts. Then the whole chain
 * gives way: the knot comes apart and drifts off, the routes blur and fade, and
 * the funnel rebuilds itself before the next cycle starts. Coordinates are
 * normalised to the stage (0–1).
 */

type Vec = { x: number; y: number };

type Route = {
  /** Where the route starts, as a fraction of the funnel's length. */
  start: number;
  /** Signed distance of the lane from the funnel's axis at the mouth, in stage heights. */
  lane: number;
  /** Per-route curvature, so the funnel reads as drawn rather than ruled. */
  bend: number;
};

type Traveller = {
  route: number;
  t: number;
  speed: number;
  /** Which way it drifts off its route when the chain gives way (unit vector). */
  drift: Vec;
};

type Queued = {
  /** Slot in the knot: depth back from the neck (0–1) and side of the axis (-1–1). */
  depth: number;
  side: number;
  wobble: number;
  /** Where it entered the knot, and how far it has eased into its slot (0–1). */
  from: Vec;
  settle: number;
};

type Comet = { route: number; t: number; travel: number };
/** One question fired from the neck at the founder: a tight bolt of particles. */
type Shot = { u: number; flight: number; bolt: { along: number; side: number }[] };
type Shard = { x: number; y: number; vx: number; vy: number; life: number; maxLife: number; ember?: boolean };
/** A piece of the knot once it comes apart. */
type Dust = { x: number; y: number; vx: number; vy: number; life: number; maxLife: number };
type Ring = { x: number; y: number; from: number; to: number; age: number; duration: number; width: number };

export type BottleneckEvent =
  | { type: "reset" }
  | { type: "hit"; count: number; interval: number }
  | { type: "burst" };

export const BOTTLENECK = {
  neck: { x: 0.64, y: 0.52 },
  founder: { x: 0.78, y: 0.52 },
} as const;

/** The funnel's mouth on the left and the half-height of its lanes there. */
const FUNNEL_LEFT = 0.02;
const FUNNEL_SPREAD = 0.42;
/** Travellers join the knot this far before the neck, as a fraction of the stage width. */
const KNOT_BACK = 0.05;
/** Seconds from an empty founder to the burst; leaves room in a 5.5 s step for what follows. */
const CHARGE_TIME = 3.9;
/** After the burst the knot comes apart and the routes blur out... */
const COLLAPSE_TIME = 1.4;
/** ...then the funnel and the founder rebuild before the next cycle. */
const REFORM_TIME = 0.8;
const START_CHARGE = 0.05;
/** Seconds before the first question of a cycle. */
const FIRST_ASK = 0.5;

/** Seconds between questions: patient at first, then a barrage just before the burst. */
const askInterval = (charge: number) => 0.07 + 0.85 * Math.pow(1 - charge, 1.7);

function seeded(seed: number) {
  let value = seed >>> 0;

  return () => {
    value = (value + 0x6d2b79f5) >>> 0;
    let mixed = Math.imul(value ^ (value >>> 15), 1 | value);
    mixed ^= mixed + Math.imul(mixed ^ (mixed >>> 7), 61 | mixed);
    return ((mixed ^ (mixed >>> 14)) >>> 0) / 4294967296;
  };
}

const clamp01 = (value: number) => Math.min(1, Math.max(0, value));
const smoothstep = (value: number) => {
  const k = clamp01(value);
  return k * k * (3 - 2 * k);
};
const easeOutCubic = (value: number) => 1 - Math.pow(1 - clamp01(value), 3);

/** 1 at the funnel's mouth, easing to 0 at the neck with a level approach at both ends. */
function spread(s: number, bend: number) {
  return 1 - smoothstep(Math.pow(clamp01(s), bend));
}

function makeRoutes(count: number): Route[] {
  const random = seeded(17);
  const bands = [0, 0.1, 0.22];

  return Array.from({ length: count }, (_, index) => {
    const u = ((index + 0.5 + (random() - 0.5) * 0.6) / count) * 2 - 1;

    return {
      start: bands[Math.floor(random() * bands.length)] + random() * 0.07,
      lane: u * FUNNEL_SPREAD,
      // Neighbouring lanes bend alike, so the routes weave a little without tangling.
      bend: 1 + 0.1 * Math.sin(index * 0.75 + 1.3) + (random() - 0.5) * 0.05,
    };
  });
}

/** A point on a route at stage x, in normalised coordinates. */
function routeAt(route: Route, x: number): Vec {
  const { neck } = BOTTLENECK;
  const s = (x - FUNNEL_LEFT) / (neck.x - FUNNEL_LEFT);
  return { x, y: neck.y + route.lane * spread(s, route.bend) };
}

function routeStartX(route: Route) {
  return FUNNEL_LEFT + route.start * (BOTTLENECK.neck.x - FUNNEL_LEFT);
}

/** A traveller's position: t runs from the route's start to the back of the knot. */
function travellerAt(route: Route, t: number): Vec {
  const x0 = routeStartX(route);
  return routeAt(route, x0 + (BOTTLENECK.neck.x - KNOT_BACK - x0) * t);
}

export type BottleneckEngine = {
  resize: () => void;
  step: (dt: number) => void;
  draw: (time: number) => void;
  settle: () => void;
};

export function createBottleneckEngine(
  canvas: HTMLCanvasElement,
  getAskers: (routeCount: number) => number[],
  emit: (event: BottleneckEvent) => void = () => {},
): BottleneckEngine | null {
  const context = canvas.getContext("2d");

  if (!context) return null;

  const ctx = context;
  const layer = document.createElement("canvas");
  const layerCtx = layer.getContext("2d");
  const random = seeded(29);
  let width = 0;
  let height = 0;
  let ratio = 1;
  let routes: Route[] = [];
  let travellers: Traveller[] = [];
  let jam: Queued[] = [];
  let comets: Comet[] = [];
  let shots: Shot[] = [];
  let shards: Shard[] = [];
  let rings: Ring[] = [];
  let dust: Dust[] = [];
  let charge = START_CHARGE;
  let phase: "charge" | "collapse" | "reform" = "charge";
  let phaseTime = 0;
  let flash = 0;
  let kick = 0;
  let bump = 0;
  let askClock = FIRST_ASK;
  let asks = 0;
  let hits = 0;
  let leakClock = 0;

  const toPx = (point: Vec) => ({ x: point.x * width, y: point.y * height });
  const scale = () => routes.length / 44;
  const bell = () => (random() + random() + random()) / 1.5 - 1;
  const founderRadius = () => Math.min(15, Math.max(9, width * 0.016));
  const strain = () => (phase === "charge" ? smoothstep((charge - 0.75) / 0.25) : 0);

  /** How swollen the knot is: it grows with the queue and starts from nothing after a collapse. */
  const pressure = () => (phase === "charge" ? charge : phase === "collapse" ? 1 : 0);

  /** How far the chain has given way after the burst (0–1). */
  const scatter = () => (phase === "collapse" ? easeOutCubic(phaseTime / COLLAPSE_TIME) : 0);

  /** How present the routes are: they blur out in the collapse and come back as the funnel rebuilds. */
  const presence = () =>
    phase === "collapse"
      ? 1 - 0.78 * scatter()
      : phase === "reform"
        ? 0.22 + 0.78 * smoothstep(phaseTime / REFORM_TIME)
        : 1;

  const knot = () => {
    const p = pressure();
    return { length: width * 0.05 * (1 + 1.2 * p), half: height * 0.045 * (1 + 1.1 * p) };
  };

  const jamTarget = () => Math.round((60 + 260 * Math.pow(pressure(), 1.2)) * scale());

  const slot = (queued: Queued, time: number, size = knot()): Vec => {
    const neck = toPx(BOTTLENECK.neck);
    // A teardrop: zero width at the neck, widest near the back where the routes arrive.
    const profile = (Math.sqrt(queued.depth) * (1 - 0.4 * queued.depth)) / 0.61;
    const shake = 0.5 + 2 * Math.min(1.3, pressure());
    const target = {
      x: neck.x - queued.depth * size.length + Math.sin(time * 3.1 + queued.wobble) * shake,
      y: neck.y + queued.side * size.half * profile + Math.cos(time * 2.6 + queued.wobble * 1.7) * shake,
    };
    const k = easeOutCubic(queued.settle);
    return { x: queued.from.x + (target.x - queued.from.x) * k, y: queued.from.y + (target.y - queued.from.y) * k };
  };

  const queue = (from: Vec, settled = false) => {
    jam.push({
      depth: 0.03 + 0.97 * Math.pow(random(), 1.4),
      side: bell() * 0.9,
      wobble: random() * Math.PI * 2,
      from,
      settle: settled ? 1 : 0,
    });
  };

  const tracePath = (target: CanvasRenderingContext2D, route: Route) => {
    const x0 = routeStartX(route);
    const steps = 48;
    target.beginPath();

    for (let index = 0; index <= steps; index += 1) {
      const point = toPx(routeAt(route, x0 + (BOTTLENECK.neck.x - x0) * (index / steps)));
      if (index === 0) target.moveTo(point.x, point.y);
      else target.lineTo(point.x, point.y);
    }
  };

  const renderRoutes = () => {
    if (!layerCtx) return;

    layer.width = canvas.width;
    layer.height = canvas.height;
    layerCtx.setTransform(ratio, 0, 0, ratio, 0, 0);
    layerCtx.clearRect(0, 0, width, height);
    layerCtx.lineWidth = 0.9;
    layerCtx.strokeStyle = "rgba(255,255,255,0.13)";
    layerCtx.fillStyle = "rgba(255,255,255,0.72)";

    for (const route of routes) {
      tracePath(layerCtx, route);
      layerCtx.stroke();
    }

    for (const route of routes) {
      const start = toPx(routeAt(route, routeStartX(route)));
      layerCtx.beginPath();
      layerCtx.arc(start.x, start.y, 1.7, 0, Math.PI * 2);
      layerCtx.fill();
    }
  };

  const build = (count: number) => {
    routes = makeRoutes(count);
    travellers = [];
    jam = [];

    routes.forEach((_, route) => {
      for (let index = 0; index < 3; index += 1) {
        const angle = random() * Math.PI * 2;
        travellers.push({
          route,
          t: (index + random()) / 3,
          speed: 0.17 + random() * 0.1,
          drift: { x: Math.cos(angle), y: Math.sin(angle) },
        });
      }
    });

    // Start with a knot already formed so the first frame reads as a bottleneck.
    for (let index = 0; index < jamTarget(); index += 1) queue({ x: 0, y: 0 }, true);
  };

  const askers = () => {
    const list = getAskers(routes.length);
    return list.length > 0 ? list : [0];
  };

  const fire = () => {
    const list = askers();
    comets.push({ route: list[asks % list.length], t: 0, travel: 1.3 - 0.8 * charge });
    shots.push({
      u: 0,
      flight: 0.3 - 0.14 * charge,
      bolt: Array.from({ length: 7 }, (_, index) => ({ along: index === 0 ? 0 : random() * 14, side: bell() * 3 })),
    });
    asks += 1;
    kick = 1;

    // Each shot is taken from the tip of the knot.
    for (let taken = 0; taken < 3 && jam.length > 30 * scale(); taken += 1) {
      let nearest = 0;
      for (let index = 1; index < jam.length; index += 1) {
        if (jam[index].depth < jam[nearest].depth) nearest = index;
      }
      jam.splice(nearest, 1);
    }
  };

  const burst = (centre: Vec, radius: number) => {
    flash = 1;
    rings.push(
      { ...centre, from: radius, to: radius * 6.5, age: 0, duration: 0.75, width: 1.5 },
      { ...centre, from: radius, to: radius * 3.8, age: -0.06, duration: 0.55, width: 1 },
    );

    // Fast streaks for the blast, then slow embers that drift up and fade.
    for (let index = 0; index < 140; index += 1) {
      const ember = index >= 100;
      const angle = random() * Math.PI * 2;
      const speed = ember ? 20 + random() * 100 : 60 + 360 * Math.pow(random(), 1.6);
      const life = ember ? 1.1 + random() * 0.9 : 0.5 + random() * 0.8;
      shards.push({
        x: centre.x + Math.cos(angle) * radius * 0.6,
        y: centre.y + Math.sin(angle) * radius * 0.6,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        life,
        maxLife: life,
        ember,
      });
    }
  };

  // The knot comes apart: every queued particle drifts off from where it sat.
  const dissolve = () => {
    const size = knot();
    const neck = toPx(BOTTLENECK.neck);
    const centre = { x: neck.x - size.length * 0.32, y: neck.y };
    const now = performance.now() / 1000;

    dust = jam.map((queued) => {
      const point = slot(queued, now, size);
      const dx = point.x - centre.x;
      const dy = point.y - centre.y;
      const distance = Math.hypot(dx, dy) || 1;
      const speed = 15 + random() * 70;
      const life = 0.8 + random() * 0.7;
      return {
        x: point.x,
        y: point.y,
        vx: (dx / distance) * speed + (random() - 0.5) * 20,
        vy: (dy / distance) * speed + (random() - 0.5) * 20,
        life,
        maxLife: life,
      };
    });
    jam = [];
    comets = [];
    shots = [];
  };

  const leak = (centre: Vec, radius: number) => {
    const angle = random() * Math.PI * 2;
    const speed = 30 + random() * 50;
    const life = 0.35 + random() * 0.35;
    shards.push({
      x: centre.x + Math.cos(angle) * radius,
      y: centre.y + Math.sin(angle) * radius,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed,
      life,
      maxLife: life,
    });
  };

  const streamEnds = () => {
    const neck = toPx(BOTTLENECK.neck);
    const founder = toPx(BOTTLENECK.founder);
    return { neck, end: { x: founder.x - founderRadius() * 0.85, y: founder.y } };
  };

  return {
    resize() {
      width = canvas.clientWidth;
      height = canvas.clientHeight;
      ratio = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = Math.round(width * ratio);
      canvas.height = Math.round(height * ratio);
      ctx.setTransform(ratio, 0, 0, ratio, 0, 0);

      if (width === 0 || height === 0) return;

      const count = width < 640 ? 26 : 44;
      if (routes.length !== count) build(count);
      renderRoutes();
    },

    step(dt) {
      const founder = toPx(BOTTLENECK.founder);
      const radius = founderRadius();
      const p = Math.min(1, pressure());
      phaseTime += dt;

      // The backlog creeps back up the funnel as the knot fills. In the collapse everything stops.
      if (phase !== "collapse") {
        const backlog = 0.82 - 0.32 * p;
        for (const traveller of travellers) {
          const queueing =
            traveller.t > backlog ? Math.max(0.14, 1 - ((traveller.t - backlog) / (1 - backlog)) * 0.86) : 1;
          traveller.t += traveller.speed * queueing * dt;

          if (traveller.t >= 1) {
            queue(toPx(travellerAt(routes[traveller.route], 1)));
            traveller.t = random() * 0.04;
          }
        }
      }

      for (const queued of jam) queued.settle = Math.min(1, queued.settle + dt * 2.6);
      const target = jamTarget();
      if (jam.length > target) jam.splice(0, jam.length - target);

      if (phase === "charge") {
        charge = Math.min(1, charge + dt / CHARGE_TIME);
        askClock -= dt;
        while (askClock <= 0) {
          askClock += askInterval(charge);
          fire();
        }

        // Near capacity the founder starts to spill over.
        leakClock += dt * 34 * strain();
        while (leakClock >= 1) {
          leakClock -= 1;
          leak(founder, radius);
        }

        if (charge >= 1) {
          phase = "collapse";
          phaseTime = 0;
          burst(founder, radius);
          dissolve();
          emit({ type: "burst" });
        }
      } else if (phase === "collapse") {
        if (phaseTime >= COLLAPSE_TIME) {
          // The funnel rebuilds: the team starts sending again from wherever they are.
          phase = "reform";
          phaseTime = 0;
          for (const traveller of travellers) traveller.t = random() * 0.85;
        }
      } else if (phaseTime >= REFORM_TIME) {
        phase = "charge";
        phaseTime = 0;
        charge = START_CHARGE;
        askClock = FIRST_ASK;
        asks = 0;
        hits = 0;
        emit({ type: "reset" });
      }

      const settle = Math.exp(-1.6 * dt);
      for (const piece of dust) {
        piece.vx *= settle;
        piece.vy *= settle;
        piece.x += piece.vx * dt;
        piece.y += piece.vy * dt;
        piece.life -= dt;
      }
      dust = dust.filter((piece) => piece.life > 0);

      // Questions reach the knot along the askers' routes and add to it.
      for (const comet of comets) {
        comet.t += dt / comet.travel;
        if (comet.t >= 1) {
          const back = toPx(travellerAt(routes[comet.route], 1));
          rings.push({ ...back, from: 3, to: 16, age: 0, duration: 0.45, width: 1 });
          for (let index = 0; index < 4; index += 1) queue({ ...back });
        }
      }
      comets = comets.filter((comet) => comet.t < 1);

      for (const shot of shots) {
        shot.u += dt / shot.flight;
        if (shot.u >= 1 && phase === "charge") {
          hits += 1;
          bump = 1;
          emit({ type: "hit", count: hits, interval: askInterval(charge) });
        }
      }
      shots = shots.filter((shot) => shot.u < 1);

      const drag = Math.exp(-2.4 * dt);
      for (const shard of shards) {
        shard.vx *= drag;
        shard.vy *= drag;
        if (shard.ember) shard.vy -= 18 * dt;
        shard.x += shard.vx * dt;
        shard.y += shard.vy * dt;
        shard.life -= dt;
      }
      shards = shards.filter((shard) => shard.life > 0);

      for (const ring of rings) ring.age += dt;
      rings = rings.filter((ring) => ring.age < ring.duration);
      flash = Math.max(0, flash - dt * 2.6);
      kick = Math.max(0, kick - dt * 9);
      bump = Math.max(0, bump - dt * 7);
    },

    draw(time) {
      ctx.clearRect(0, 0, width, height);

      if (width === 0 || height === 0) return;

      const neck = toPx(BOTTLENECK.neck);
      const founder = toPx(BOTTLENECK.founder);
      const radius = founderRadius();
      const size = knot();
      const pressed = Math.min(1.6, pressure());
      const list = askers();

      const present = presence();
      const loosened = scatter();
      ctx.globalAlpha = present;
      ctx.drawImage(layer, 0, 0, width, height);

      // The askers' routes, a shade brighter than the rest.
      for (const routeIndex of new Set(list)) {
        const route = routes[routeIndex];
        if (!route) continue;
        tracePath(ctx, route);
        ctx.strokeStyle = "rgba(255,255,255,0.26)";
        ctx.lineWidth = 1;
        ctx.stroke();
        const start = toPx(routeAt(route, routeStartX(route)));
        ctx.beginPath();
        ctx.arc(start.x, start.y, 2.6, 0, Math.PI * 2);
        ctx.fillStyle = "#ffffff";
        ctx.fill();
      }

      // Travellers slip off their routes and blur out when the chain gives way.
      for (const traveller of travellers) {
        const point = toPx(travellerAt(routes[traveller.route], traveller.t));
        ctx.beginPath();
        ctx.arc(
          point.x + traveller.drift.x * loosened * 26,
          point.y + traveller.drift.y * loosened * 26,
          1.1 + loosened * 0.6,
          0,
          Math.PI * 2,
        );
        ctx.fillStyle = `rgba(255,255,255,${0.5 + 0.45 * traveller.t})`;
        ctx.fill();
      }
      ctx.globalAlpha = 1;

      ctx.globalCompositeOperation = "lighter";

      // The knot: a glow stretched along the flow that thickens with the queue. It goes out
      // when the knot comes apart and returns as the funnel rebuilds.
      const held =
        phase === "charge" ? 1 : phase === "collapse" ? 1 - smoothstep(phaseTime / 0.6) : smoothstep(phaseTime / REFORM_TIME);
      const knotCentre = { x: neck.x - size.length * 0.32, y: neck.y };
      ctx.save();
      ctx.translate(knotCentre.x, knotCentre.y);
      ctx.scale(1.7, 1);
      const knotGlow = ctx.createRadialGradient(0, 0, 0, 0, 0, size.half * 1.9);
      knotGlow.addColorStop(0, `rgba(255,255,255,${(0.08 + 0.12 * pressed) * held})`);
      knotGlow.addColorStop(1, "rgba(255,255,255,0)");
      ctx.fillStyle = knotGlow;
      ctx.beginPath();
      ctx.arc(0, 0, size.half * 1.9, 0, Math.PI * 2);
      ctx.fill();
      ctx.restore();

      ctx.fillStyle = "rgba(255,255,255,0.8)";
      for (const queued of jam) {
        const point = slot(queued, time, size);
        ctx.beginPath();
        ctx.arc(point.x, point.y, 1.2, 0, Math.PI * 2);
        ctx.fill();
      }

      for (const piece of dust) {
        ctx.beginPath();
        ctx.arc(piece.x, piece.y, 1.2, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,255,255,${0.85 * Math.pow(piece.life / piece.maxLife, 1.3)})`;
        ctx.fill();
      }

      // The neck: the one point everything passes through, kicking with every shot.
      const neckRadius = 9 + 5 * pressed + 6 * kick;
      const neckGlow = ctx.createRadialGradient(neck.x, neck.y, 0, neck.x, neck.y, neckRadius);
      neckGlow.addColorStop(0, `rgba(255,255,255,${(0.55 + 0.25 * Math.min(1, pressed) + 0.2 * kick) * held})`);
      neckGlow.addColorStop(1, "rgba(255,255,255,0)");
      ctx.fillStyle = neckGlow;
      ctx.beginPath();
      ctx.arc(neck.x, neck.y, neckRadius, 0, Math.PI * 2);
      ctx.fill();

      // Once the shots come faster than they land, they run together into one stream.
      const { end } = streamEnds();
      const merge = phase === "charge" ? smoothstep((charge - 0.72) / 0.25) : 0;
      if (merge > 0) {
        const beam = ctx.createLinearGradient(neck.x, neck.y, end.x, end.y);
        beam.addColorStop(0, `rgba(255,255,255,${0.45 * merge})`);
        beam.addColorStop(1, `rgba(255,255,255,${0.3 * merge})`);
        ctx.strokeStyle = beam;
        ctx.lineWidth = 1 + 2.5 * merge;
        ctx.beginPath();
        ctx.moveTo(neck.x, neck.y);
        ctx.lineTo(end.x, end.y);
        ctx.stroke();
      }

      ctx.lineCap = "round";
      for (const shot of shots) {
        const along = Math.pow(clamp01(shot.u), 1.3);
        const head = { x: neck.x + (end.x - neck.x) * along, y: neck.y };
        for (const piece of shot.bolt) {
          const x = Math.max(neck.x, head.x - piece.along);
          const y = head.y + piece.side * Math.sin(Math.PI * clamp01(shot.u * 0.85 + 0.15));
          ctx.strokeStyle = `rgba(255,255,255,${0.85 - piece.along * 0.03})`;
          ctx.lineWidth = 1.2;
          ctx.beginPath();
          ctx.moveTo(Math.max(neck.x, x - 9), y);
          ctx.lineTo(x, y);
          ctx.stroke();
        }
        const bolt = ctx.createRadialGradient(head.x, head.y, 0, head.x, head.y, 8);
        bolt.addColorStop(0, "rgba(255,255,255,0.55)");
        bolt.addColorStop(1, "rgba(255,255,255,0)");
        ctx.fillStyle = bolt;
        ctx.beginPath();
        ctx.arc(head.x, head.y, 8, 0, Math.PI * 2);
        ctx.fill();
      }

      // The founder fills up, flinches at every hit, strains near capacity, then bursts.
      if (phase === "charge") {
        const fill = Math.pow(charge, 1.1);
        const tension = strain();
        const shake = tension * 1.4 + bump * 0.8;
        const centre = {
          x: founder.x + (Math.sin(time * 71) + 0.6 * Math.sin(time * 43.3)) * shake,
          y: founder.y + (Math.cos(time * 67) + 0.6 * Math.cos(time * 39.7)) * shake,
        };
        const flicker = 1 - tension * 0.5 * (0.5 + 0.5 * Math.sin(time * 47 + 3 * Math.sin(time * 13)));
        const glowRadius = radius * (2 + 2.2 * fill + 0.6 * bump);
        const halo = ctx.createRadialGradient(centre.x, centre.y, 0, centre.x, centre.y, glowRadius);
        halo.addColorStop(0, `rgba(255,255,255,${(0.12 + 0.4 * fill + 0.2 * bump) * flicker})`);
        halo.addColorStop(1, "rgba(255,255,255,0)");
        ctx.fillStyle = halo;
        ctx.beginPath();
        ctx.arc(centre.x, centre.y, glowRadius, 0, Math.PI * 2);
        ctx.fill();

        ctx.globalCompositeOperation = "source-over";
        ctx.beginPath();
        ctx.arc(centre.x, centre.y, radius * (1 + 0.06 * tension * Math.sin(time * 38) + 0.05 * bump), 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(255,255,255,${0.35 + 0.5 * fill + 0.15 * bump})`;
        ctx.lineWidth = 1;
        ctx.stroke();

        ctx.beginPath();
        ctx.arc(centre.x, centre.y, Math.min(radius, radius * (0.36 + 0.64 * fill) * (1 + 0.14 * bump)), 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,255,255,${0.8 + 0.2 * flicker})`;
        ctx.fill();
        ctx.globalCompositeOperation = "lighter";
      } else {
        const reform = phase === "reform" ? smoothstep(phaseTime / REFORM_TIME) : 0;

        ctx.globalCompositeOperation = "source-over";
        ctx.beginPath();
        ctx.arc(founder.x, founder.y, radius * 0.3, 0, Math.PI * 2);
        ctx.fillStyle = "#3f3f46";
        ctx.fill();

        ctx.setLineDash([2, 3.5]);
        ctx.beginPath();
        ctx.arc(founder.x, founder.y, radius * (0.9 + 0.1 * reform), 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(255,255,255,${0.28 * (1 - reform)})`;
        ctx.lineWidth = 1;
        ctx.stroke();
        ctx.setLineDash([]);

        if (reform > 0) {
          ctx.beginPath();
          ctx.arc(founder.x, founder.y, radius, 0, Math.PI * 2);
          ctx.strokeStyle = `rgba(255,255,255,${0.35 * reform})`;
          ctx.stroke();
        }
        ctx.globalCompositeOperation = "lighter";
      }

      // Each question travels its asker's route into the knot as a comet.
      for (const comet of comets) {
        const route = routes[comet.route];
        if (!route) continue;

        for (let index = 0; index < 14; index += 1) {
          const t = comet.t - index * 0.01;
          const next = comet.t - (index + 1) * 0.01;
          if (next < 0) break;
          const a = toPx(travellerAt(route, t));
          const b = toPx(travellerAt(route, next));
          ctx.strokeStyle = `rgba(255,255,255,${0.85 * (1 - index / 14)})`;
          ctx.lineWidth = 2 - index * 0.1;
          ctx.beginPath();
          ctx.moveTo(a.x, a.y);
          ctx.lineTo(b.x, b.y);
          ctx.stroke();
        }

        const point = toPx(travellerAt(route, comet.t));
        const glow = ctx.createRadialGradient(point.x, point.y, 0, point.x, point.y, 9);
        glow.addColorStop(0, "rgba(255,255,255,0.45)");
        glow.addColorStop(1, "rgba(255,255,255,0)");
        ctx.fillStyle = glow;
        ctx.beginPath();
        ctx.arc(point.x, point.y, 9, 0, Math.PI * 2);
        ctx.fill();
        ctx.beginPath();
        ctx.arc(point.x, point.y, 2.2, 0, Math.PI * 2);
        ctx.fillStyle = "#ffffff";
        ctx.fill();
      }

      for (const ring of rings) {
        if (ring.age < 0) continue;
        const progress = ring.age / ring.duration;
        ctx.beginPath();
        ctx.arc(ring.x, ring.y, ring.from + (ring.to - ring.from) * easeOutCubic(progress), 0, Math.PI * 2);
        ctx.strokeStyle = `rgba(255,255,255,${0.75 * Math.pow(1 - progress, 1.6)})`;
        ctx.lineWidth = ring.width;
        ctx.stroke();
      }

      ctx.lineWidth = 1.2;
      for (const shard of shards) {
        const life = Math.pow(shard.life / shard.maxLife, 1.2);

        if (shard.ember) {
          ctx.beginPath();
          ctx.arc(shard.x, shard.y, 1.3, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(255,255,255,${0.85 * life})`;
          ctx.fill();
        } else {
          ctx.strokeStyle = `rgba(255,255,255,${life})`;
          ctx.beginPath();
          ctx.moveTo(shard.x - shard.vx * 0.035, shard.y - shard.vy * 0.035);
          ctx.lineTo(shard.x + 0.01, shard.y + 0.01);
          ctx.stroke();
        }
      }

      if (flash > 0) {
        // A hard white pop at the founder inside a wide, fading blast of light.
        const pop = 1 - flash;
        const burstGlow = ctx.createRadialGradient(founder.x, founder.y, 0, founder.x, founder.y, radius * 8);
        burstGlow.addColorStop(0, `rgba(255,255,255,${0.95 * Math.pow(flash, 1.3)})`);
        burstGlow.addColorStop(0.25, `rgba(255,255,255,${0.35 * Math.pow(flash, 1.5)})`);
        burstGlow.addColorStop(1, "rgba(255,255,255,0)");
        ctx.fillStyle = burstGlow;
        ctx.beginPath();
        ctx.arc(founder.x, founder.y, radius * 8, 0, Math.PI * 2);
        ctx.fill();

        if (pop < 0.4) {
          ctx.beginPath();
          ctx.arc(founder.x, founder.y, radius * (1 + 1.4 * easeOutCubic(pop / 0.4)), 0, Math.PI * 2);
          ctx.fillStyle = `rgba(255,255,255,${0.9 * (1 - pop / 0.4)})`;
          ctx.fill();
        }
      }

      ctx.globalCompositeOperation = "source-over";
      ctx.lineCap = "butt";
    },

    settle() {
      // A still frame for reduced motion: a swollen knot with the backlog up the funnel,
      // questions on their way in and out, and a founder running hot but intact.
      charge = 0.72;
      phase = "charge";
      phaseTime = 0;
      dust = [];
      flash = 0;
      kick = 0;
      bump = 0;
      shards = [];
      rings = [];
      travellers.forEach((traveller, index) => {
        traveller.t = (index * 0.37) % 1;
      });
      jam = [];
      for (let index = 0; index < jamTarget(); index += 1) queue({ x: 0, y: 0 }, true);
      const list = askers();
      comets = [0.35, 0.7].map((t, index) => ({ route: list[index % list.length], t, travel: 1 }));
      shots = [0.25, 0.6, 0.9].map((u) => ({
        u,
        flight: 1,
        bolt: Array.from({ length: 7 }, (_, index) => ({ along: index === 0 ? 0 : random() * 14, side: bell() * 3 })),
      }));
    },
  };
}
